// The rectangle a run is framed on: its whole level, not the corner of it
// that has been revealed.
//
// **A level's ground exists before it is on the board.** Regions open as
// objectives complete, so at the moment a run starts `Position.bounds` is the
// opening region alone — and framing on it drops the player into a close-up of
// one lobe with no sense of the map they are in. Every region's cells are
// known from the level the whole time; what is missing is only that they are
// not `cells` yet.
//
// The **union** with the board, never the level alone, for the case the level
// cannot describe: camps arrive at a stage boundary and grow the board past
// anything the author drew, so a resumed run is routinely larger than its own
// level.
//
// Undefined level, or a level with no ground, gives the board back unchanged —
// which is every scenario, every versus game and every hot-seat board.
//
// This is the rules half of what was `framedBounds` in `src/client/ai/render`
// (day 2 of the client split). How much of that union the camera is willing
// to show is the client's call and stayed there.

import { type Bounds, fileOf, rankOf } from "src/engine/rules/board";

export function levelBounds(
  board: Bounds,
  level?: { regions: readonly { cells: readonly number[] }[] }
): Bounds {
  if (!level) return board;
  let out = board;
  for (const region of level.regions) {
    for (const cell of region.cells) {
      const file = fileOf(cell);
      const rank = rankOf(cell);
      out =
        file >= out.minFile && file <= out.maxFile && rank >= out.minRank && rank <= out.maxRank
          ? out
          : {
              minFile: Math.min(out.minFile, file),
              maxFile: Math.max(out.maxFile, file),
              minRank: Math.min(out.minRank, rank),
              maxRank: Math.max(out.maxRank, rank)
            };
    }
  }
  return out;
}
